import { z } from "zod";

// Validation schemas -- projected from spec domain model

export const createPlayerSchema = z
  .object({
    first_name: z.string().max(255).optional(),
    surname: z.string().max(255).optional(),
    nickname: z.string().max(100).optional(),
    date_of_birth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be YYYY-MM-DD format"),
    family_code: z
      .string()
      .max(50, "Family code must be 50 characters or fewer")
      .optional(),
    active: z.boolean().optional(),
    player_code: z.string().max(100, "Player code must be 100 characters or fewer").optional(),
    grade: z
      .number()
      .int()
      .min(1, "Grade must be between 1 and 12")
      .max(12, "Grade must be between 1 and 12")
      .optional()
      .nullable(),
    enrollment_year: z
      .number()
      .int()
      .min(2000, "Enrollment year must be 2000 or later")
      .optional()
      .nullable(),
    enrollments: z
      .array(
        z.object({
          sport_id: z.string(),
          indemnity_complete: z.boolean().optional(),
          also_eligible_for: z.array(z.string()).optional(),
          club_id: z.string().nullable().optional(), // 149 — club affiliation
          age_group_override_id: z.string().nullable().optional(), // 158
          age_group_override_reason: z.string().max(500).nullable().optional(), // 158
        }),
      )
      .optional(),
  })
  .refine((data) => (data.first_name?.trim() || data.surname?.trim()), {
    message: "At least one of first name or surname is required",
  });

export const updatePlayerSchema = z
  .object({
    first_name: z.string().max(255).optional(),
    surname: z.string().max(255).optional(),
    nickname: z.string().max(100).optional(),
    date_of_birth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be YYYY-MM-DD format")
      .optional(),
    family_code: z
      .string()
      .max(50, "Family code must be 50 characters or fewer")
      .optional()
      .nullable(),
    active: z.boolean().optional(),
    player_code: z.string().max(100, "Player code must be 100 characters or fewer").optional().nullable(),
    grade: z
      .number()
      .int()
      .min(1, "Grade must be between 1 and 12")
      .max(12, "Grade must be between 1 and 12")
      .optional()
      .nullable(),
    enrollment_year: z
      .number()
      .int()
      .min(2000, "Enrollment year must be 2000 or later")
      .optional()
      .nullable(),
    enrollments: z
      .array(
        z.object({
          sport_id: z.string(),
          indemnity_complete: z.boolean().optional(),
          also_eligible_for: z.array(z.string()).optional(),
          club_id: z.string().nullable().optional(), // 149 — club affiliation
          age_group_override_id: z.string().nullable().optional(), // 158
          age_group_override_reason: z.string().max(500).nullable().optional(), // 158
        }),
      )
      .optional(),
  });

export const enrollPlayerSchema = z.object({
  sport_id: z.string(),
  indemnity_complete: z.boolean().optional(),
  indemnity_document_url: z.string().max(2048).nullable().optional(), // 155
  also_eligible_for: z.array(z.string()).optional(),
  club_id: z.string().nullable().optional(), // 149 — club affiliation for the season
  age_group_override_id: z.string().nullable().optional(), // 158
  age_group_override_reason: z.string().max(500).nullable().optional(), // 158
});

export const updateEnrollmentSchema = z.object({
  indemnity_complete: z.boolean().optional(),
  indemnity_document_url: z.string().max(2048).nullable().optional(), // 155
  also_eligible_for: z.array(z.string()).optional(),
  club_id: z.string().nullable().optional(), // 149 — club affiliation for the season
  age_group_override_id: z.string().nullable().optional(), // 158
  age_group_override_reason: z.string().max(500).nullable().optional(), // 158
});

export type CreatePlayerInput = z.infer<typeof createPlayerSchema>;
export type UpdatePlayerInput = z.infer<typeof updatePlayerSchema>;

/** Compute display name from first_name + surname */
export function playerDisplayName(
  firstName: string | null | undefined,
  surname: string | null | undefined,
): string {
  return [firstName, surname].filter(Boolean).join(" ") || "Unnamed";
}
